import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { trackEvent } from "../lib/api";
import { getPreview, createCheckoutSession } from "../lib/api/index";
import { logger } from "../lib/logger";
import PreviewLoading from "../components/preview/PreviewLoading";
import PreviewError from "../components/preview/PreviewError";
import PreviewBusinessHealth from "../components/preview/PreviewBusinessHealth";
import PreviewSupportingScores from "../components/preview/PreviewSupportingScores";
import PreviewWeaknesses from "../components/preview/PreviewWeaknesses";
import PreviewCategories from "../components/preview/PreviewCategories";
import PreviewUnlockCTA from "../components/preview/PreviewUnlockCTA";
import BenchmarkWidget from "../components/shared/BenchmarkWidget";

// Phase 2.5 — Experiment C1 (value-framing headline above unlock CTA).
// Deterministic 50/50 A/B. The backend is authoritative — `preview` API
// returns `experiment_assignments.preview_value_headline` ∈ VARIANTS.
// The frontend NEVER computes its own variant (tamperable in DevTools,
// not stable across devices). It only reads + renders.
const EXPERIMENT_PVH = "preview_value_headline";
const VARIANT_CONTROL = "c0_no_headline";
const VARIANT_HEADLINE = "c1_recover_range";

function buildHeadline(min, max, variant) {
  // Returns { visible: bool, mode: "range" | "single", min, max }.
  //  - control arm                → never visible
  //  - variant arm, both 0/missing → not visible
  //  - variant arm, min===max     → single (use max)
  //  - variant arm, min===0       → single (use max)
  //  - otherwise                  → range
  if (variant !== VARIANT_HEADLINE) {
    return { visible: false, mode: null, min: 0, max: 0 };
  }
  const minN = Number(min) || 0;
  const maxN = Number(max) || 0;
  if (minN === 0 && maxN === 0) return { visible: false, mode: null, min: 0, max: 0 };
  if (minN === maxN || minN === 0) {
    return { visible: true, mode: "single", min: maxN, max: maxN };
  }
  return { visible: true, mode: "range", min: minN, max: maxN };
}

export default function AuditPreview() {
  const { sessionId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [error, setError] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  // Phase 5a-fix — preview polling loop. Hits /audit/preview, which now
  // returns either 200 (report ready) or 425 (status="generating") while
  // the detached scoring + AI task runs server-side. Caps at 60 attempts
  // (~3 min at 3s intervals) so a stuck task can't keep the UI hanging.
  useEffect(() => {
    trackEvent("preview_view", `/audit/preview/${sessionId}`, {}, sessionId);
    let cancelled = false;
    let timeoutId = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 60;

    const poll = async () => {
      attempts += 1;
      try {
        const data = await getPreview(sessionId);
        if (cancelled) return;
        // 200 OR 425 both come through axios success path (axios treats
        // anything >=200 <300 as success by default; 425 lands in the
        // catch branch below).
        setReport(data);
        setGenerating(false);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const payload = err?.response?.data;
        if (status === 425 && payload?.status === "generating") {
          setGenerating(true);
          setPollAttempts(attempts);
          if (attempts >= MAX_ATTEMPTS) {
            setError("Your audit is taking longer than expected. Refresh to try again.");
            setLoading(false);
            return;
          }
          const retryAfter = Number(payload.retry_after_seconds) || 3;
          timeoutId = setTimeout(poll, retryAfter * 1000);
          return;
        }
        logger.error("preview_load_failed", { sessionId, status, err: err?.message });
        if (status === 404) setError("We couldn't find that audit. The link may be old, or the audit may not be started yet. Start a fresh audit from the home page.");
        else setError("Something on our side broke. Refresh in a moment, or email help@systemsforgeai.com if the problem keeps happening.");
        setLoading(false);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId]);

  // Destructure must come before the hooks below so they can depend on
  // engine-provided loss values + the server-assigned variant. `report || {}`
  // keeps this safe during the initial loading render when `report === null`.
  const {
    detected_weaknesses = [],
    leak_categories_affected = [],
    experiment_assignments,
    estimated_monthly_loss_min,
    estimated_monthly_loss_max,
    // --- P1 Business Health additive payload ---
    business_health,
    revenue_recovery,
    opportunity,
    automation,
    operations,
    retention,
    executive_summary,
  } = report || {};

  // Supporting-tier display order is CEO-locked. Map a fallback (legacy
  // sessions without P1 blocks) to a minimal stub so the page still renders
  // without crashing.
  const supportingScores = [
    { id: "revenue_recovery",  metricLabel: "Revenue Recovery",        block: revenue_recovery },
    { id: "opportunity",       metricLabel: "Opportunity",             block: opportunity },
    { id: "automation",        metricLabel: "Automation Readiness",    block: automation },
    { id: "operations",        metricLabel: "Operations",              block: operations },
    { id: "retention",         metricLabel: "Retention",               block: retention },
  ].filter((s) => s.block && typeof s.block.score === "number");

  // Server-authoritative variant. Defaults to control until the API
  // response lands — keeps the headline invisible during the loading
  // window so we never show-then-hide.
  const assignedVariant = experiment_assignments?.[EXPERIMENT_PVH] ?? VARIANT_CONTROL;

  // Phase 2.5 / Experiment C1 — value-framing headline above the unlock CTA.
  // Pure derivation from engine values + assigned variant. Must run on every
  // render (no early returns above) to satisfy hook ordering.
  const headline = useMemo(
    () => buildHeadline(estimated_monthly_loss_min, estimated_monthly_loss_max, assignedVariant),
    [estimated_monthly_loss_min, estimated_monthly_loss_max, assignedVariant],
  );

  const onUnlock = async () => {
    if (unlocking) return;
    setUnlocking(true);
    try {
      // unlock_click carries the actually-assigned variant so the funnel
      // analysis can attribute the click to the correct arm. The
      // `unlocking` flag is a per-component-instance in-flight guard
      // against double-submit during the Stripe redirect; NOT a
      // per-session suppression. A refresh + retry will fire a second
      // unlock_click, which keeps the 1:1 unlock_click → checkout_started
      // correspondence and the rate math valid.
      await trackEvent(
        "unlock_click",
        `/audit/preview/${sessionId}`,
        { headline_variant: assignedVariant },
        sessionId,
      );
      const data = await createCheckoutSession({
        session_id: sessionId,
        origin: window.location.origin,
      });
      // Redirect to Stripe-hosted checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      logger.error("unlock_failed", { sessionId, err: err?.response?.data || err?.message });
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 || detail === "already_paid") {
        // Already paid → send them straight to the report
        window.location.href = `/audit/report/${sessionId}`;
      } else if (err?.response?.status === 429) {
        setError("Too many checkout attempts. Please wait a few minutes and try again.");
      } else {
        setError("Could not start checkout. Please try again in a moment.");
      }
      setUnlocking(false);
    }
  };

  // Fire `preview_value_headline_view` exactly when the assignment is
  // observed for THIS session — once per assigned arm:
  //   - control arm: fires on first successful report load (variant string
  //     resolved to "c0_no_headline"). Without this, control has no view
  //     denominator and the lift math has no opposing arm.
  //   - variant arm: fires when the headline actually renders (visible flag
  //     flips true). Re-fires across sessionId changes only.
  // Declared above the early-return branches so hook order is stable.
  useEffect(() => {
    if (!report) return;
    if (assignedVariant === VARIANT_HEADLINE && !headline.visible) return;
    trackEvent(
      "preview_value_headline_view",
      `/audit/preview/${sessionId}`,
      {
        variant: assignedVariant,
        headline_variant: assignedVariant,
        loss_min: headline.min,
        loss_max: headline.max,
        mode: headline.mode,
      },
      sessionId,
    );
  }, [
    sessionId, report, assignedVariant,
    headline.visible, headline.min, headline.max, headline.mode,
  ]);

  if (loading || generating) {
    return <PreviewLoading pollAttempts={pollAttempts} />;
  }

  if (error && !report) {
    return <PreviewError error={error} />;
  }

  return (
    <div className="sf-container" style={{ padding: "48px 24px", maxWidth: 920 }} data-testid="preview-page">
      <div className="sf-eyebrow" style={{ marginBottom: 12 }}>SystemForge AI &middot; Diagnostic Preview</div>
      <h1 className="sf-display" style={{ fontSize: 40, lineHeight: 1.1, marginBottom: 14 }}>
        Your Business Health Audit.
      </h1>
      <p className="sf-muted-text" style={{ marginBottom: 36, maxWidth: 720 }}>
        This is what your business looks like to the diagnostic engine. The
        score below is your starting line. The full 13-section playbook tells
        you exactly what to fix and in what order — one-time $97.
      </p>

      <div style={{ display: "block" }} data-testid="preview-hierarchy-root">
        {/* P1 — Business Health is the FIRST visual element below page chrome.
           Legacy "Revenue Leak Score" + "Estimated Monthly Loss" cards were
           removed in P1 — Business Health is the master metric; Revenue
           Recovery (a supporting band below) carries the same revenue signal
           in the CEO-locked hierarchy, and monthly loss is referenced by the
           Executive Summary paragraph. API still emits the legacy fields for
           backward compat — this is a UI-only removal. */}
      </div>

      <p className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", lineHeight: 1.6, marginBottom: 20, letterSpacing: "0.02em" }} data-testid="preview-diagnostic-intent">
        This is a diagnostic, not a verdict. Your score is a starting line, not a grade.
      </p>

      <PreviewBusinessHealth
        businessHealth={business_health}
        executiveSummary={executive_summary}
      />

      <PreviewSupportingScores supportingScores={supportingScores} />

      <BenchmarkWidget score={business_health?.score} testidPrefix="preview" />

      <PreviewWeaknesses detectedWeaknesses={detected_weaknesses} />

      <PreviewCategories
        leakCategoriesAffected={leak_categories_affected}
        estimatedMonthlyLossMin={estimated_monthly_loss_min}
        estimatedMonthlyLossMax={estimated_monthly_loss_max}
      />

      <PreviewUnlockCTA
        headline={headline}
        assignedVariant={assignedVariant}
        error={error}
        unlocking={unlocking}
        onUnlock={onUnlock}
      />

      <p className="sf-mono" style={{ fontSize: 11, color: "var(--sf-muted)", lineHeight: 1.7, marginTop: 36, letterSpacing: "0.02em", maxWidth: 720 }} data-testid="preview-engine-disclosure">
        Your scores come from a deterministic engine that maps your 12 answers to 8 system categories. The AI you read narrates the engine&#39;s findings; it does not calculate them.
      </p>
    </div>
  );
}
